/**
 * Navigation Context
 *
 * Provides role-based navigation data throughout the app.
 * Computes sidebar client-side using eptai-app-components navigation module.
 *
 * Features:
 * - Instant sidebar display from cache on page load
 * - Background recomputation when user roles change
 * - Automatic cache invalidation when roles differ
 * - Session-based caching in localStorage
 */

import { getSidebar, type NavSidebarSectionData, type UserRole } from '@eptai/eptai-app-components';
import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type JSX,
} from 'react';
import { useAppAuth } from './AppContext';

// ============================================================
// Types
// ============================================================

interface Auth0User {
  ept_roles?: string[];
  email?: string;
  sub?: string;
}

interface CachedNavigation {
  sidebar: NavSidebarSectionData[];
  roles: string[];
  timestamp: number;
  userId?: string;
}

interface NavigationContextValue {
  /** Sidebar sections to render */
  sidebar: NavSidebarSectionData[];
  /** Whether navigation is loading (no cache available) */
  isLoading: boolean;
  /** Whether navigation is being recomputed in background */
  isRecomputing: boolean;
  /** User's current roles */
  userRoles: UserRole[];
  /** Force recompute the navigation */
  recompute: () => void;
  /** Clear navigation cache */
  clearCache: () => void;
}

// ============================================================
// Constants
// ============================================================

const CACHE_KEY = 'eptai_navigation_cache';
const CACHE_VERSION = 'v1';
const CACHE_TTL_MS = 24 * 60 * 60 * 1000; // 24 hours max TTL

// Valid roles that the navigation module understands
const VALID_ROLES: Set<string> = new Set([
  'Admin',
  'User',
  'AIPerformanceManager',
  'AI Sales Pro',
  'EptAdmin'
]);

// ============================================================
// Cache Helpers
// ============================================================

function getCacheKey(userId?: string): string {
  return `${CACHE_KEY}_${CACHE_VERSION}_${userId || 'anonymous'}`;
}

function loadFromCache(userId?: string): CachedNavigation | null {
  try {
    const key = getCacheKey(userId);
    const cached = localStorage.getItem(key);
    if (!cached) return null;

    const parsed: CachedNavigation = JSON.parse(cached);

    // Check TTL
    if (Date.now() - parsed.timestamp > CACHE_TTL_MS) {
      localStorage.removeItem(key);
      return null;
    }

    return parsed;
  } catch (error) {
    console.warn('[NavigationContext] Failed to load from cache:', error);
    return null;
  }
}

function saveToCache(sidebar: NavSidebarSectionData[], roles: string[], userId?: string): void {
  try {
    const key = getCacheKey(userId);
    const data: CachedNavigation = {
      sidebar,
      roles,
      timestamp: Date.now(),
      userId,
    };
    localStorage.setItem(key, JSON.stringify(data));
  } catch (error) {
    console.warn('[NavigationContext] Failed to save to cache:', error);
  }
}

function clearCacheForUser(userId?: string): void {
  try {
    const key = getCacheKey(userId);
    localStorage.removeItem(key);
  } catch (error) {
    console.warn('[NavigationContext] Failed to clear cache:', error);
  }
}

/**
 * Deep compare two sidebar arrays for equality
 */
function areSidebarsEqual(a: NavSidebarSectionData[], b: NavSidebarSectionData[]): boolean {
  if (a.length !== b.length) return false;

  for (let i = 0; i < a.length; i++) {
    const sectionA = a[i];
    const sectionB = b[i];

    if (sectionA.name !== sectionB.name) return false;
    if (sectionA.items.length !== sectionB.items.length) return false;

    for (let j = 0; j < sectionA.items.length; j++) {
      const itemA = sectionA.items[j];
      const itemB = sectionB.items[j];

      if (itemA.name !== itemB.name || itemA.route !== itemB.route || itemA.icon !== itemB.icon) {
        return false;
      }
    }
  }

  return true;
}

/**
 * Check if roles arrays are equal
 */
function areRolesEqual(a: string[], b: string[]): boolean {
  if (a.length !== b.length) return false;
  const sortedA = [...a].sort();
  const sortedB = [...b].sort();
  return sortedA.every((role, i) => role === sortedB[i]);
}

// ============================================================
// Context
// ============================================================

const NavigationContext = createContext<NavigationContextValue | undefined>(undefined);

// ============================================================
// Provider
// ============================================================

interface NavigationProviderProps {
  children: React.ReactNode;
}

export function NavigationProvider({ children }: NavigationProviderProps): JSX.Element {
  const { user, isAuthenticated, isLoading: authLoading } = useAppAuth();

  const [sidebar, setSidebar] = useState<NavSidebarSectionData[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isRecomputing, setIsRecomputing] = useState(false);
  const [cachedRoles, setCachedRoles] = useState<string[]>([]);

  // Extract and validate user roles
  const userRoles = useMemo<UserRole[]>(() => {
    const typedUser = user as Auth0User | undefined;
    const roles = typedUser?.ept_roles || [];

    console.log('[NavigationContext] Raw ept_roles from user:', roles);

    // Map backend roles to frontend roles for sidebar
    const mappedRoles = new Set<string>();
    for (const role of roles) {
      if (role === 'EptAdmin') {
        // EptAdmin is superadmin — grant all sidebar roles
        VALID_ROLES.forEach(r => mappedRoles.add(r));
      } else if (role === 'EptOperations') {
        mappedRoles.add('Admin');
      }
      if (VALID_ROLES.has(role)) {
        mappedRoles.add(role);
      }
    }

    const filteredRoles = Array.from(mappedRoles) as UserRole[];

    console.log('[NavigationContext] Mapped roles for sidebar:', filteredRoles);

    return filteredRoles;
  }, [user]);

  const userId = useMemo(() => {
    const typedUser = user as Auth0User | undefined;
    return typedUser?.sub || typedUser?.email;
  }, [user]);

  // Stable, value-based signature of the roles. Effects/callbacks depend on this
  // string rather than the `userRoles` array reference so they only run when the
  // role values actually change (not on every render). Prevents the
  // "Maximum update depth exceeded" re-render loop.
  const userRolesKey = useMemo(() => [...userRoles].sort().join(','), [userRoles]);

  /**
   * Compute sidebar from roles using the navigation module
   */
  const computeSidebar = useCallback((roles: UserRole[]): NavSidebarSectionData[] => {
    if (roles.length === 0) {
      return [];
    }

    try {
      const computed = getSidebar(roles);

      // Filter out User Profile from sidebar (now in header)
      return computed
        .map(section => ({
          ...section,
          items: section.items.filter(
            item => item.route !== '/user-profile' && item.name !== 'User Profile'
          ),
        }))
        .filter(section => section.items.length > 0);
    } catch (error) {
      console.error('[NavigationContext] Failed to compute sidebar:', error);
      return [];
    }
  }, []);

  /**
   * Force recompute the navigation
   */
  const recompute = useCallback(() => {
    if (userRoles.length === 0) return;

    setIsRecomputing(true);

    // Use requestIdleCallback for non-blocking computation
    const compute = () => {
      const newSidebar = computeSidebar(userRoles);

      // Functional update avoids depending on `sidebar` (which would re-create
      // this callback every render and re-trigger dependent effects).
      setSidebar(prev => (areSidebarsEqual(prev, newSidebar) ? prev : newSidebar));
      saveToCache(newSidebar, userRoles, userId);
      setCachedRoles(userRoles);
      setIsRecomputing(false);
    };

    if ('requestIdleCallback' in window) {
      requestIdleCallback(compute, { timeout: 1000 });
    } else {
      setTimeout(compute, 0);
    }
  }, [userRoles, userId, computeSidebar]);

  /**
   * Clear navigation cache
   */
  const clearCache = useCallback(() => {
    clearCacheForUser(userId);
    setSidebar([]);
    setCachedRoles([]);
    setIsLoading(true);
  }, [userId]);

  // ============================================================
  // Initial Load: Show cached data immediately
  // ============================================================
  useEffect(() => {
    if (authLoading) return;

    if (!isAuthenticated) {
      // Not authenticated - clear state
      setSidebar([]);
      setCachedRoles([]);
      setIsLoading(false);
      return;
    }

    // Try to load from cache first for instant display
    const cached = loadFromCache(userId);

    if (cached && cached.sidebar.length > 0) {
      console.log('[NavigationContext] Loaded sidebar from cache');
      setSidebar(cached.sidebar);
      setCachedRoles(cached.roles);
      setIsLoading(false);

      // Check if roles have changed - if so, we need to recompute
      if (!areRolesEqual(cached.roles, userRoles)) {
        console.log('[NavigationContext] Roles changed, recomputing...');
        setIsRecomputing(true);
      }
    } else if (userRoles.length > 0) {
      // No cache available - compute immediately
      console.log('[NavigationContext] No cache, computing sidebar...');
      const newSidebar = computeSidebar(userRoles);
      setSidebar(newSidebar);
      setCachedRoles(userRoles);
      saveToCache(newSidebar, userRoles, userId);
      setIsLoading(false);
    } else {
      setIsLoading(false);
    }
  }, [authLoading, isAuthenticated, userId]); // Intentionally not including userRoles here

  // ============================================================
  // Background Recompute: When roles change or recompute is triggered
  // ============================================================
  useEffect(() => {
    if (authLoading || !isAuthenticated || userRoles.length === 0) {
      return;
    }

    // Check if roles have changed from what we have cached
    if (areRolesEqual(cachedRoles, userRoles)) {
      return;
    }

    console.log('[NavigationContext] Roles changed, recomputing in background...');
    setIsRecomputing(true);

    // Use requestIdleCallback for non-blocking background computation
    const compute = () => {
      const newSidebar = computeSidebar(userRoles);

      // Functional update: compare against the latest state instead of listing
      // `sidebar` as a dependency (which caused the effect to re-run every time
      // it set the sidebar -> "Maximum update depth exceeded" loop).
      setSidebar(prev => (areSidebarsEqual(prev, newSidebar) ? prev : newSidebar));
      saveToCache(newSidebar, userRoles, userId);
      setCachedRoles(userRoles);
      setIsRecomputing(false);
    };

    if ('requestIdleCallback' in window) {
      requestIdleCallback(compute, { timeout: 1000 });
    } else {
      setTimeout(compute, 0);
    }
    // Keyed on the role *values* (userRolesKey) and userId, not the array
    // reference, so this only runs when roles genuinely change.
  }, [authLoading, isAuthenticated, userRolesKey, userId]);

  // ============================================================
  // Context Value
  // ============================================================
  const value = useMemo<NavigationContextValue>(
    () => ({
      sidebar,
      isLoading,
      isRecomputing,
      userRoles,
      recompute,
      clearCache,
    }),
    [sidebar, isLoading, isRecomputing, userRoles, recompute, clearCache]
  );

  return <NavigationContext.Provider value={value}>{children}</NavigationContext.Provider>;
}

// ============================================================
// Hook
// ============================================================

/**
 * Default context value for when the provider isn't available yet.
 * This can happen during HMR or initial render.
 */
const defaultContextValue: NavigationContextValue = {
  sidebar: [],
  isLoading: true,
  isRecomputing: false,
  userRoles: [],
  recompute: () => {},
  clearCache: () => {},
};

/**
 * Hook to access navigation context.
 * Must be used within a NavigationProvider.
 *
 * @example
 * ```tsx
 * function MySidebar() {
 *   const { sidebar, isLoading } = useNavigation();
 *
 *   if (isLoading) return <LoadingSkeleton />;
 *
 *   return (
 *     <nav>
 *       {sidebar.map(section => (
 *         <div key={section.name}>
 *           <h3>{section.name}</h3>
 *           {section.items.map(item => (
 *             <a href={item.route}>{item.name}</a>
 *           ))}
 *         </div>
 *       ))}
 *     </nav>
 *   );
 * }
 * ```
 */
export function useNavigation(): NavigationContextValue {
  const context = useContext(NavigationContext);

  // Return default context if provider is missing (handles HMR edge cases)
  if (context === undefined) {
    return defaultContextValue;
  }

  return context;
}

export default NavigationContext;
